<template>
  <div>

    <!-- XOL-464: hoisted to the component root, above every other branch. A
         strict violation can arrive alongside an empty or errored schedule —
         under `errored`, `issues` is [] (nothing set it yet) so this renders
         nothing; under `isEmpty` it can still have something to say, and the
         alternative (leaving it inside the `v-else v-loading` block) meant a
         strict-position violation that emits zero rows landed on isEmpty and
         showed "No fee timeline yet for this sharer." with no explanation at
         all. Costs nothing in the branches where it has nothing to show. -->
    <sharer-billing-issues :issues="issues" />

    <el-alert
      v-if="errored"
      type="error"
      :closable="false"
      show-icon
      title="Couldn't load the fee timeline"
      description="Please try again in a moment." />

    <el-row v-else-if="isEmpty">
      <el-col :span="24" align="center">
        <i class="text-muted">{{ reason || 'No fee timeline yet for this sharer.' }}</i>
      </el-col>
    </el-row>

    <div v-else v-loading="loading">

      <!-- XOL-416/XOL-433: the reason banner. ONE alert above the totals, not a
           marker on each of 228 rows: the condition is a property of the
           agreement, not of any single month.
           Renders the API's `reason` VERBATIM and carries no title of its own.
           It used to be titled "No amount can be calculated for this sharer",
           which was true only for the unpriced case. XOL-433 makes this fire for
           `no_rate` and `no_billing_start` too, where the rows underneath hold
           REAL stored amounts — a heading claiming no amount can be calculated,
           sitting directly above a table of amounts, would contradict itself.
           The API already sends one correct sentence per code; a client-side
           title would be a second source of copy that has to be true for all
           three, which is either vague or wrong for one of them. -->
      <el-alert
        v-if="showReasonBanner"
        type="error"
        :closable="false"
        show-icon
        :title="reason" />

      <!-- Totals header -->
      <el-row :gutter="16" class="timeline-totals">
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label">Billed to date</div>
          <div class="timeline-totals__value">{{ totalsDisplay.billedToDate }}</div>
        </el-col>
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label timeline-totals__label--muted">Projected remaining</div>
          <div class="timeline-totals__value timeline-totals__value--muted">{{ totalsDisplay.projectedRemaining }}</div>
        </el-col>
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label">Lifetime</div>
          <div class="timeline-totals__value timeline-totals__value--bold">{{ totalsDisplay.lifetime }}</div>
        </el-col>
      </el-row>
      <p v-if="isUnpriced" class="timeline-caption text-muted">
        Totals can't be calculated while the Sharer # is 0 — this is not the same as zero.
      </p>
      <!-- XOL-464: a strict violation (position or term) nulls all three totals
           but leaves `reasonCode` null, so `isUnpriced` is false for it — do NOT
           widen `isUnpriced` to cover this. That would print "while the Sharer #
           is 0" on a sharer whose number is perfectly fine, manufacturing the
           exact false statement this branch exists to avoid. Gate on the totals
           themselves instead (`totalsUnknown`), independent of why they're null. -->
      <p v-else-if="totalsUnknown" class="timeline-caption text-muted">
        Totals can't be calculated while any month is unpriced — a dash is not the same as zero.
      </p>
      <p v-else class="timeline-caption text-muted">
        Amounts left of "Lifetime" are billed actuals; "Projected remaining" is forward-estimated and may change.
      </p>

      <!-- Open-ended note -->
      <el-alert
        v-if="openEnded"
        type="info"
        :closable="false"
        show-icon
        title=""
        description="This agreement is open-ended — there's no expiry date, so there's no fixed end to project to. The table runs to the end of next month, or to the end of the sharer rate's term when that is later." />

      <el-table
        :data="displayRows"
        :max-height="520"
        style="width:100%"
        :row-class-name="rowClassName">

        <!-- Expand column -->
        <el-table-column type="expand">
          <template slot-scope="scope">
            <div class="timeline-expand">

              <!-- XOL-520 addendum (2026-09-22) — uniform line items on every period.
                   Gated on the DERIVED flag `hasLineItems` (segments.length > 0), never
                   `isSplit` (segments.length > 1) and never `tier_split` directly, so a
                   one-tier month and a two-tier month render identical markup and the
                   SPA still degrades cleanly ahead of XOL-519 — a payload with no
                   `segments` key at all has `hasLineItems` false and this whole block is
                   absent, indistinguishable from `main`. See the `Charged`/`Full month`
                   fallback below the determinant caption, which is what carries the
                   charged range on THAT path.
                   These are the segments block's OWN el-rows, siblings of — and rendered
                   ABOVE — the determinant `el-row` below. They must never become el-cols
                   folded into that row: dropped in there they'd flow into its 3-up grid
                   and interleave with Tier / Position / Billing mode. -->
              <template v-if="scope.row.hasLineItems">
                <!-- D-A1 (Carlos, 2026-09-22): ONE heading for every row, a constant
                     string — not a computed branch. Supersedes both the earlier
                     "Tiers this month" instruction and the UX spec's tiered/tier-less
                     copy pair: Carlos's own framing is that "Charges" covers the
                     override row too, since its line item IS a charge, it simply has
                     no tier. Do not reintroduce a heading computed or `_anyTierNamed`
                     — neither is needed for this. -->
                <p class="timeline-segments__heading">Charges this month</p>

                <!-- D.2 — the part-month caption now renders on a prorated SINGLE-tier
                     month too (Carlos's own Sep 2031 screenshot), not just the compose
                     case. Independent of `isSplit` — Trap 1: prorated and split are
                     independent facts about a month and must stay independent v-ifs,
                     never one nested inside the other. `_composeCaption` is
                     pre-formatted to '' when it must not render (prorated but either
                     label came back empty), so a half-sentence ("only  is charged")
                     can never appear. -->
                <p v-if="scope.row._composeCaption" class="timeline-segments__caption">
                  {{ scope.row._composeCaption }}
                </p>

                <!-- Deliberately no :md="8" like the determinant grid below: a
                     segment is a five-field record, wider than a key/value pair,
                     and stays 2-up at every width above `xs` — including at `md`
                     (≥992px), where the determinant grid goes 3-up. Do not "fix"
                     this into a 3-up grid to match. -->
                <el-row
                  v-for="(seg, segIdx) in scope.row._segments"
                  :key="segIdx"
                  :gutter="8"
                  class="timeline-segments__row">
                  <el-col :xs="24" :sm="12" class="timeline-segments__tier-col">
                    <!-- D-A1 / D.6: no chip element at all when a segment names no
                         tier (the override row) — never a chip reading "T—", never an
                         empty grey box. A zero-background spacer holds the column
                         width so ranges stay aligned when a tiered and an override
                         panel are open together. The accessible carrier for "this row
                         has no tier" is the `Tier` determinant below, which D-A3 keeps
                         unconditional for exactly this reason — a spacer announces
                         nothing, which is correct here: stating a negative would be
                         noise the visual doesn't show. -->
                    <span v-if="seg.tierLabel" class="timeline-segments__tier">{{ seg.tierLabel }}</span>
                    <span v-else class="timeline-segments__tier-spacer"></span>
                    <span class="timeline-segments__range">{{ seg.rangeLabel }}</span>
                  </el-col>
                  <el-col :xs="24" :sm="12" class="timeline-segments__basis-col">
                    <span class="timeline-segments__basis">{{ seg.basisLabel }}</span>
                    <span class="timeline-segments__amount">{{ seg.amountLabel }}</span>
                  </el-col>
                </el-row>

                <!-- D-A4 (Carlos): "keep it. Looks good." — Month total renders on
                     one-line blocks too, unconditionally within this block. Binds to
                     the row's own `amountLabel` — the SAME pre-formatted string the
                     collapsed Amount cell renders. Never a sum of the segments above:
                     the API rounds once on the total and allocates parts
                     largest-remainder, so summing the display-rounded parts can land a
                     cent off (see the plan's Trap 2 / mutation test B1). Binding the
                     identical string is what makes this panel reconcile against the
                     collapsed row by construction, on EVERY row, not just split ones. -->
                <el-row :gutter="8" class="timeline-segments__total-row">
                  <el-col :xs="12" :sm="12" class="timeline-segments__total-label">Month total</el-col>
                  <el-col :xs="12" :sm="12" class="timeline-segments__total-amount">{{ scope.row.amountLabel }}</el-col>
                </el-row>

                <!-- Footnote — split-only (C12). This gate is a STRUCTURAL segment
                     count (`isSplit`, segments.length > 1), never a comparison of
                     sum(parts) against the total — Trap 2 forbids the latter, and a
                     reviewer should challenge exactly this distinction: `isSplit` never
                     touches the money values, only how many segments there are.
                     Unconditional within every split block — never gated on whether the
                     parts happen to re-sum. Carlos decided (XOL-520 Question B) that a
                     restated billing is not marked, so this sentence stays true
                     everywhere: the SPA never shows a total other than the one the API
                     sent. -->
                <p v-if="scope.row.isSplit" class="timeline-segments__footnote">
                  Tier amounts are rounded for display; the month total is the amount billed.
                </p>
              </template>

              <!-- D.5 — moved here (immediately above the determinant grid), copy and
                   gate unchanged. It used to open the whole panel; now that a summed
                   line-item block renders above it on every row, "never summed" needs
                   to sit directly over the figures it is disclaiming, not over the top
                   of the panel. -->
              <p class="timeline-expand__caption text-muted">
                Determinant values are shown for explanation only and are never summed.
              </p>

              <el-row :gutter="8">
                <!-- Tiering group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Tier</span>
                  <!-- XOL-520: `_tierLabel` renders "1 · 2" on a split month — NOT
                       "T1 · T2". The T1/T2 chips in the segments block above are a
                       different element (the tier CHIP grammar); this grid item's key
                       already reads "Tier", so the bare joined integers are
                       unambiguous. Do not "unify" these two representations. -->
                  <span class="timeline-expand__val">{{ scope.row._tierLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">{{ positionKeyLabel(scope.row) }}</span>
                  <span class="timeline-expand__val">{{ scope.row._positionLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Billing mode</span>
                  <span class="timeline-expand__val">{{ scope.row.billing_mode !== null && scope.row.billing_mode !== undefined ? scope.row.billing_mode : '—' }}</span>
                </el-col>

                <!-- Fees group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Telco fee</span>
                  <span class="timeline-expand__val">{{ scope.row._telcoFeeLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">MW rate</span>
                  <span class="timeline-expand__val">{{ scope.row._mwRateLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">RRU rate</span>
                  <span class="timeline-expand__val">{{ scope.row._rruRateLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">RRU set</span>
                  <span class="timeline-expand__val">{{ scope.row._rruSetLabel }}</span>
                </el-col>

                <!-- Sharer-specific group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">CAPEX</span>
                  <span class="timeline-expand__val">{{ scope.row._capexLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">OPEX</span>
                  <span class="timeline-expand__val">{{ scope.row._opexLabel }}</span>
                </el-col>
                <!-- XOL-548: hidden when the discount is zero, as the Sharer Agreement
                     editor hides its Discount field. See `_showDiscount`. -->
                <el-col v-if="scope.row._showDiscount" :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Discount</span>
                  <span class="timeline-expand__val">{{ scope.row._discountLabel }}</span>
                </el-col>

                <!-- No "First-sharer-only" item (removed 2026-09-23, Carlos). The flag
                     was removed from sharer rates by XOL-487 (SPA, #242: the rate
                     form's checkbox) and XOL-486 (API #248: sharer_rates.firstSharerOnly and
                     rate_versions.firstSharerOnly dropped). A rate for Sharer #1 alone
                     is now fromSharer = toSharer = 1. The fee ledger still SENDS
                     `first_sharer_only` as per-cycle billing history, and new rows
                     are hardcoded to false, so this grid showed "No" on every row
                     for a setting that no longer exists. Display only: do not
                     strip the key from the payload or the fixture. -->

                <!-- Invoice info (invoiced rows only) -->
                <template v-if="scope.row.status === 'invoiced'">
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Invoice</span>
                    <span class="timeline-expand__val">{{ scope.row.invoice_id !== null && scope.row.invoice_id !== undefined ? scope.row.invoice_id : '—' }}</span>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Invoiced</span>
                    <span class="timeline-expand__val">{{ scope.row.invoiced_at ? formatDate(scope.row.invoiced_at) : '—' }}</span>
                  </el-col>
                </template>

                <!-- Prorated info (prorated rows only) — XOL-520 addendum D-A2.
                     REMOVAL IS CONDITIONAL on `!hasLineItems`, and that predicate is
                     the whole safety net. Against the new API every prorated row
                     carries `segments`, `hasLineItems` is true, and this block never
                     renders — the part-month caption above (inside the hasLineItems
                     block) is its replacement, and `charge_from`/`charge_to` stay
                     consumed there via `chargeRangeLabel`. Against today's API (no
                     `segments` key at all — XOL-519 unmerged), `hasLineItems` is false
                     and this is the ONLY carrier of the charged range. Simplifying this
                     to a bare `v-if="scope.row.isProrated"` would silently delete the
                     charged range from every prorated row the moment the new API ships.
                     No API change: the API keeps emitting charge_from/charge_to either
                     way (Carlos: "no need to remove charge from the API"). -->
                <template v-if="scope.row.isProrated && !scope.row.hasLineItems">
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Charged</span>
                    <span class="timeline-expand__val">{{ scope.row.chargeRangeLabel }}</span>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Full month</span>
                    <span class="timeline-expand__val">{{ scope.row.fullMonthLabel }}</span>
                  </el-col>
                </template>

              </el-row>
            </div>
          </template>
        </el-table-column>

        <!-- Period column -->
        <el-table-column label="Period" min-width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.__boundary" class="timeline-boundary-marker">Projected from here →</span>
            {{ scope.row.periodLabel }}
          </template>
        </el-table-column>

        <!-- Status column -->
        <el-table-column label="Status" width="160">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.status === 'invoiced'"
              type="success"
              size="small"
              effect="light">
              <i class="fa fa-check-circle"></i> Invoiced
            </el-tag>
            <el-tag
              v-else-if="scope.row.status === 'billed-uninvoiced'"
              type="warning"
              size="small"
              effect="light">
              <i class="fa fa-clock"></i> Billed
            </el-tag>
            <el-tag
              v-else
              type="info"
              size="small"
              effect="light">
              <i class="fa fa-hourglass-half"></i> Projected
            </el-tag>
          </template>
        </el-table-column>

        <!-- Amount column -->
        <el-table-column align="right" class-name="fee-cell" min-width="140">
          <!-- XOL-416: the "Unpriced" marker lives in the HEADER, once, rather than
               beside every dashed cell. Colour is never the only signal — the word,
               the em-dash in each cell and the banner's icon each carry it alone. -->
          <!-- `slot-scope` is required even though the scope is unused: el-table-column
               only picks up the header slot as a SCOPED slot. Without it the slot is
               silently ignored and the column renders with no title at all. -->
          <template slot="header" slot-scope="scope">
            Amount
            <!-- XOL-464: gated on whether any ROW is actually dashed
                 (`anyUnpricedRow`), not on `isUnpriced` — a strict violation
                 dashes the tail of the table via `priced: false` same as the
                 unassigned-Sharer-# case, but `isUnpriced` stays false for it
                 (§ caption above), which used to leave the dashed rows with no
                 column-level marker at all. -->
            <el-tag
              v-if="anyUnpricedRow"
              type="danger"
              size="mini"
              effect="light"
              class="timeline-flag"
              aria-label="Unpriced — no amount can be calculated for one or more months.">Unpriced</el-tag>
          </template>
          <template slot-scope="scope">
            {{ scope.row.amountLabel }}
            <el-tooltip
              v-if="scope.row.isProrated"
              content="Prorated — partial month; see details for the charged range."
              placement="top">
              <el-tag
                type="info"
                size="mini"
                effect="light"
                class="timeline-flag"
                aria-label="Prorated — partial month; see details for the charged range.">
                <i class="fa fa-adjust"></i>
              </el-tag>
            </el-tooltip>
            <el-tooltip
              v-if="scope.row.isLocked"
              content="Rate locked — this month uses a manual override, not the calculated rate."
              placement="top">
              <el-tag
                type="warning"
                size="mini"
                effect="light"
                class="timeline-flag"
                aria-label="Rate locked — this month uses a manual override, not the calculated rate.">
                <i class="fa fa-lock"></i>
              </el-tag>
            </el-tooltip>
            <!-- XOL-520: third flag, source-order after Prorated and Rate-locked, so
                 an unsplit row's cell is byte-identical to today. Independent v-if —
                 never v-else-if — because `prorated` and `isSplit` co-occur on the
                 compose case (both tags render together). Worded, not icon-only: the
                 user's question ("am I billed at two tiers?") must be answerable
                 without hovering. -->
            <el-tooltip
              v-if="scope.row.isSplit"
              :content="scope.row._splitTooltip"
              placement="top">
              <el-tag
                type="info"
                size="mini"
                effect="plain"
                class="timeline-flag"
                :aria-label="scope.row._splitTooltip">2 tiers</el-tag>
            </el-tooltip>
          </template>
        </el-table-column>

      </el-table>
    </div>
  </div>
</template>

<script>
import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'
import {toAppTime} from '@/lib/appTime'

import SharerBillingIssues from './SharerBillingIssues'


export default {
  name: 'sharer-fee-timeline',

  mixins: [NumberMixins, PostMixins, DateMixins],

  components: {
    SharerBillingIssues
  },

  props: {
    id: {
      type: [Number, String],
      required: true
    },
    started: {
      type: String,
      default: null
    }
  },

  data: function () {
    return {
      rows: [],
      totals: null,
      window: null,
      licensee: null,
      reason: null,
      // XOL-416: machine-readable sibling of `reason`. Switch on THIS, never on
      // the prose (a literal sentence that changes with any copy edit) and never
      // on the sharer's number — see `isUnpriced`.
      reasonCode: null,
      // XOL-462: strict_position_out_of_range / strict_term_exceeded, absent on
      // any API not carrying the field yet — never render "no issues" from a
      // missing key or a failed fetch.
      issues: [],
      loading: false,
      loaded: false,
      errored: false
    }
  },

  computed: {
    isEmpty: function () {
      return this.loaded && !this.errored && this.rows.length === 0
    },

    openEnded: function () {
      return !!(this.window && this.window.openEnded)
    },

    boundaryIndex: function () {
      for (var i = 0; i < this.rows.length; i++) {
        if (this.rows[i].source === 'projection') {
          return i
        }
      }
      return -1
    },

    hasProjection: function () {
      return this.boundaryIndex >= 0
    },

    displayRows: function () {
      var self = this
      var boundary = self.boundaryIndex
      return self.rows.map(function (row, idx) {
        var periodLabel = ''
        if (row.period) {
          var oPeriod = toAppTime(row.period + '-01')
          periodLabel = (oPeriod === null) ? '' : oPeriod.format('MMM YYYY')
        }

        // XOL-416: an UNPRICED month carries `billed_amount: 0` — the API's honest
        // answer to "what was due this month" for a month that WAS due. Formatting
        // that 0 prints RM0.00, which a finance user reads as "this sharer is free"
        // — the opposite of the truth, which is that we cannot compute the price.
        // So the amount is never the signal; `priced` is.
        //
        // Strictly `=== false`, not falsy: a response predating the flag has no
        // `priced` key at all, and `undefined` must keep formatting normally rather
        // than dashing every historical row.
        var unpricedRow = row.priced === false

        var amountLabel
        if (unpricedRow || row.billed_amount === null || row.billed_amount === undefined) {
          amountLabel = '—'
        } else {
          amountLabel = self.formatMoney(row.billed_amount, 2)
        }

        var fullMonthLabel
        if (unpricedRow || row.full_month_amount === null || row.full_month_amount === undefined) {
          fullMonthLabel = '—'
        } else {
          fullMonthLabel = self.formatMoney(row.full_month_amount, 2)
        }

        var chargeRangeLabel = ''
        if (row.prorated && row.charge_from && row.charge_to) {
          chargeRangeLabel = self.formatDate(row.charge_from) + ' – ' + self.formatDate(row.charge_to)
        }

        // XOL-520: the split flag is DERIVED, never read off `row.tier_split`
        // directly — split <=> `segments` is an array with more than one entry.
        // This is what lets the SPA merge ahead of XOL-519: an old payload with
        // no `segments` key (or one with a single entry) renders exactly as
        // today, with zero visual change.
        var isSplit = Array.isArray(row.segments) && row.segments.length > 1
        var seg0 = isSplit ? row.segments[0] : null
        var seg1 = isSplit ? row.segments[1] : null

        // XOL-520 addendum (2026-09-22) — uniform line items on every period.
        // INDEPENDENTLY derived from `row.segments`, never defined in terms of
        // `isSplit` (and never the reverse) and never read off `row.tier_split`.
        // This is the merge-order safety property: the API half (XOL-519) is still
        // unmerged, so a reader must be able to look at THIS line alone and see
        // that a missing `segments` key yields `false`, without cross-referencing
        // `isSplit` above. `isSplit` = more than one segment (drives the "2 tiers"
        // tag, the footnote, and the tier-join grammar); `hasLineItems` = at least
        // one segment (drives the whole line-item block, including the one-line
        // case that is now the majority of rows).
        var hasLineItems = Array.isArray(row.segments) && row.segments.length > 0

        // Small money formatters local to this row. `money2` never consults
        // `unpricedRow` — it mirrors the existing (ungated) `_telcoFeeLabel` /
        // `_mwRateLabel` / `_rruRateLabel` rule below. `moneyUnpriced` mirrors
        // `fullMonthLabel`'s existing unpriced-aware rule. Neither ever prints
        // RM0.00 for a null/undefined value (XOL-416 failure mode) — both dash.
        var money2 = function (v) {
          return (v === null || v === undefined) ? '—' : self.formatMoney(v, 2)
        }
        var moneyUnpriced = function (v) {
          return (unpricedRow || v === null || v === undefined) ? '—' : self.formatMoney(v, 2)
        }
        var rawOrDash = function (v) {
          return (v === null || v === undefined) ? '—' : v
        }

        // Money determinants
        var _telcoFeeLabel = row.telco_fee !== null && row.telco_fee !== undefined
          ? self.formatMoney(row.telco_fee, 2) : '—'
        var _mwRateLabel = row.mw_rate !== null && row.mw_rate !== undefined
          ? self.formatMoney(row.mw_rate, 2) : '—'
        var _rruRateLabel = row.rru_rate !== null && row.rru_rate !== undefined
          ? self.formatMoney(row.rru_rate, 2) : '—'
        var _rruSetLabel = rawOrDash(row.rru_set)
        var _capexLabel = row.capex !== null && row.capex !== undefined
          ? self.formatMoney(row.capex, 2) : '—'
        var _opexLabel = row.opex !== null && row.opex !== undefined
          ? self.formatMoney(row.opex, 2) : '—'

        // XOL-520 D3 — the determinant grid stops half-truthing on a split month.
        // `segments[0]` mirrors the flat fields by API contract (mandatory:
        // tier_seq, full_month_amount), so Tier / Telco fee / Full month join
        // unconditionally when split. MW rate / RRU rate / RRU set are NOT
        // mandatory on segment 2 — absent (`undefined`) means the API did not
        // supply it there, and the fix is to fall back to today's flat single
        // value, never to join a real value against a dash. `null` on segment 2
        // is "supplied and empty" and DOES join, rendering the em-dash half.
        var _tierLabel
        if (!isSplit) {
          _tierLabel = (row.tier_seq !== null && row.tier_seq !== undefined) ? row.tier_seq : '—'
        } else {
          _tierLabel = rawOrDash(seg0.tier_seq) + ' · ' + rawOrDash(seg1.tier_seq)
        }

        // "Telco fee" has no per-segment field of its own — its per-tier
        // equivalent is `segment.full_month_amount` (plan D3). Not unpriced-aware,
        // matching the ungated `_telcoFeeLabel` rule above.
        if (isSplit) {
          _telcoFeeLabel = money2(seg0.full_month_amount) + ' · ' + money2(seg1.full_month_amount)
        }

        // "Full month" (rendered only inside the `isProrated` block, so its join
        // is reachable only on the compose case) joins the SAME per-segment field,
        // unpriced-aware, matching the flat `fullMonthLabel` rule above.
        if (isSplit) {
          fullMonthLabel = moneyUnpriced(seg0.full_month_amount) + ' · ' + moneyUnpriced(seg1.full_month_amount)
        }

        // XOL-520 F-A1 (Carlos, 2026-09-23): MW rate / RRU rate / RRU set show ONE
        // value when both segments carry the same raw value, and join only when
        // they differ. These three live on each tier, but every multi-tier rate
        // in the DB today has the same value on every tier: 60 of 64 carry the
        // defaults 0 / 0 / 3 and the other 4 carry 0 / 0 / 0. So an unconditional
        // join always printed "RM0.00 · RM0.00". The rate editor does let a user
        // save different values per tier, so a real difference must still show both.
        //
        // Compares the RAW values, never the formatted strings (XOL-380: formatted
        // money is display-only). Two empties are "the same" and collapse to one
        // dash; an empty against a value is a difference and joins. Tier and
        // Telco fee are deliberately NOT collapsed: on a split month the tiers
        // always differ, and Telco fee is the figure the split is about.
        var sameRaw = function (a, b) {
          var aEmpty = (a === null || a === undefined)
          var bEmpty = (b === null || b === undefined)
          if (aEmpty || bEmpty) { return aEmpty && bEmpty }
          return Math.abs(Number(a) - Number(b)) < 0.00005
        }

        // `seg1.x !== undefined` stays the outer gate: absent on segment 2 still
        // means "use the flat value" (the API's load-bearing absent/null rule).
        if (isSplit && seg1.mw_rate !== undefined) {
          _mwRateLabel = sameRaw(seg0.mw_rate, seg1.mw_rate)
            ? money2(seg0.mw_rate)
            : money2(seg0.mw_rate) + ' · ' + money2(seg1.mw_rate)
        }
        if (isSplit && seg1.rru_rate !== undefined) {
          _rruRateLabel = sameRaw(seg0.rru_rate, seg1.rru_rate)
            ? money2(seg0.rru_rate)
            : money2(seg0.rru_rate) + ' · ' + money2(seg1.rru_rate)
        }
        if (isSplit && seg1.rru_set !== undefined) {
          _rruSetLabel = sameRaw(seg0.rru_set, seg1.rru_set)
            ? rawOrDash(seg0.rru_set)
            : rawOrDash(seg0.rru_set) + ' · ' + rawOrDash(seg1.rru_set)
        }

        // XOL-520 addendum (2026-09-22) — the segments block's pre-formatted rows,
        // now built whenever there is at least one segment (`hasLineItems`), not
        // only when split. `days_in_month` is the proration denominator A3
        // confirmed (`FeePeriodGenerator::computePeriod()` always divides by the
        // calendar month's length, in both the plain-split and compose cases) —
        // printed VERBATIM, never derived locally from `period`: a locally-computed
        // denominator that silently disagrees with the API's is worse than not
        // rendering the equation at all.
        //
        // Trap 3: `days_in_month` / `chargeable_days` (segment `days`) are nullable
        // on rows predating XOL-472, and this path is now reachable on every priced
        // row, not just the ~7 split ones. Rule: never dash INSIDE the equation
        // ("8 of — days") — when either count is unknown, drop the whole day-count
        // clause and fall back to a full-month phrasing (C6), or to no basis text
        // at all when also unpriced (C7).
        var _segments = []
        var _splitDate = null
        var _splitTooltip = ''
        var _composeCaption = ''
        if (hasLineItems) {
          var daysInMonthKnown = row.days_in_month !== null && row.days_in_month !== undefined
          var denomLabel = daysInMonthKnown ? self.formatInteger(row.days_in_month) : null

          _segments = row.segments.map(function (seg) {
            // D-A1 / D.6: '' — not "T—" — when the segment names no tier (the
            // override row). The template renders NO chip element at all in that
            // case, never a chip reading "T—" and never an empty grey box.
            var tierLabel = (seg.tier_seq !== null && seg.tier_seq !== undefined)
              ? ('T' + seg.tier_seq) : ''
            var rangeLabel = (seg.from && seg.to)
              ? (self.formatDate(seg.from) + ' – ' + self.formatDate(seg.to)) : '—'

            var daysKnown = daysInMonthKnown && (seg.days !== null && seg.days !== undefined)
            var basisLabel
            if (daysKnown) {
              // C4 / C5 — both counts known: the full equation, priced or not.
              // XOL-416: unpriced drops the "× rate" clause entirely rather than
              // printing "× —" — a rate that cannot be shown is not the same fact
              // as a rate of zero, and "× —" still LOOKS like an equation with a
              // missing operand. Dates and day counts are facts, not prices, and
              // still render either way.
              basisLabel = self.formatInteger(seg.days) + ' of ' + denomLabel + ' days'
              if (!unpricedRow) {
                basisLabel += ' × ' + money2(seg.full_month_amount)
              }
            } else if (!unpricedRow) {
              // C6 — priced but either day count is null: fall back to a
              // full-month phrasing rather than a half-equation with a dashed
              // denominator.
              basisLabel = money2(seg.full_month_amount) + ' per full month'
            } else {
              // C7 — unpriced AND day counts unknown: no basis text at all.
              basisLabel = ''
            }

            var amountLabel
            if (unpricedRow || seg.billed_amount === null || seg.billed_amount === undefined) {
              amountLabel = '—'
            } else {
              amountLabel = self.formatMoney(seg.billed_amount, 2)
            }
            return {
              tierLabel: tierLabel,
              rangeLabel: rangeLabel,
              basisLabel: basisLabel,
              amountLabel: amountLabel
            }
          })

          // Split-only facts below — split_date and the collapsed row's tooltip.
          // Both stay at their empty defaults on a one-segment row; `seg1` is null
          // there.
          if (isSplit) {
            // A1: keep BOTH `split_date` and `segments[1].from` — both are emitted
            // facts and are redundant by construction. `undefined`/`null` on
            // `split_date` falls back to `segments[1].from` rather than being
            // derived with half-open-boundary arithmetic client-side (XOL-414).
            _splitDate = (row.split_date !== null && row.split_date !== undefined)
              ? row.split_date : (seg1 && seg1.from !== undefined ? seg1.from : null)

            // Load-bearing: names BOTH ranges rather than "T1 until the 16th", so
            // it stays true on the compose case where segment 1 does not start on
            // the 1st of the month. Never implies the segments tile the whole
            // month.
            var range1 = (seg0.from && seg0.to)
              ? (self.formatDate(seg0.from) + ' – ' + self.formatDate(seg0.to)) : ''
            var range2 = (_splitDate && seg1.to)
              ? (self.formatDate(_splitDate) + ' – ' + self.formatDate(seg1.to)) : ''
            _splitTooltip = 'Two tiers this month — T' + rawOrDash(seg0.tier_seq) + ' charged ' + range1 +
              ', then T' + rawOrDash(seg1.tier_seq) + ' charged ' + range2 +
              '; see details for each tier\'s amount.'
          }

          // D.2 / C3 — the part-month caption. Pre-formatted to '' unless prorated
          // AND both labels are non-empty, so a half-sentence ("only  is charged")
          // can never render — see the template's `v-if="scope.row._composeCaption"`.
          // Independent of `isSplit` (Trap 1): a prorated single-tier month gets
          // this caption too.
          if (row.prorated && chargeRangeLabel && periodLabel) {
            _composeCaption = 'Part-billed month — only ' + chargeRangeLabel + ' is charged, not the whole of ' +
              periodLabel + '. Day counts below are out of the full month.'
          }
        }

        // Discount: money or %. XOL-548: `discount_percentage` truthy means a FLAT
        // amount, despite the column name. That is how the Sharer Agreement editor
        // (FinanceSharer.vue: currency prefix when set, % suffix when not), the
        // invoice line-item form (SiteInvoiceAction.vue, which computes
        // `flag ? flat : amount × d / 100`) and the stored invoice history all read
        // it. This label used to read it the other way round and showed a flat RM900
        // as "900%". Keep all three readings in step; the column name is the outlier.
        var _discountLabel
        if (row.discount === null || row.discount === undefined) {
          _discountLabel = '—'
        } else if (row.discount_percentage) {
          _discountLabel = self.formatMoney(row.discount, 2)
        } else {
          _discountLabel = row.discount + '%'
        }
        // XOL-548 (Carlos, 2026-09-28): a zero discount hides the Discount item
        // instead of reading "0%" or "RM0.00". Most licensees have none (the column
        // defaults to 0), and a zero has no type to show. This mirrors the editor
        // (FinanceSharer.vue, `v-if="details.discount || editing"`). A null discount
        // is missing data, not "no discount", so it still shows "—" like the rest
        // of the grid.
        var _showDiscount = !(row.discount !== null && row.discount !== undefined &&
                              Number(row.discount) === 0)

        // XOL-376 Inc 2: post-clamp billed position, surfaced inline when it differs
        // from the raw position. null billed_position (override) or equal → raw only.
        var _positionLabel
        if (row.position === null || row.position === undefined) {
          _positionLabel = '—'
        } else if (row.billed_position !== null && row.billed_position !== undefined &&
                   row.billed_position !== row.position) {
          _positionLabel = row.position + ' · billed at #' + row.billed_position
        } else {
          _positionLabel = row.position
        }

        return Object.assign({}, row, {
          periodLabel: periodLabel,
          amountLabel: amountLabel,
          fullMonthLabel: fullMonthLabel,
          isProjection: row.source === 'projection',
          isProrated: !!row.prorated,
          isLocked: !!row.rate_locked,
          // XOL-520: derived, not `!!row.tier_split` — see the comment on `isSplit`
          // above. This is the gate that drives the "2 tiers" tag, the footnote and
          // the tier-join grammar.
          isSplit: isSplit,
          // XOL-520 addendum: derived, independently of `isSplit` — see the comment
          // above. This is the gate that drives the whole line-item block (heading,
          // one-or-more segment lines, Month total) and the `Charged`/`Full month`
          // fallback's negation.
          hasLineItems: hasLineItems,
          chargeRangeLabel: chargeRangeLabel,
          __boundary: boundary >= 0 && idx === boundary,
          _telcoFeeLabel: _telcoFeeLabel,
          _mwRateLabel: _mwRateLabel,
          _rruRateLabel: _rruRateLabel,
          _capexLabel: _capexLabel,
          _opexLabel: _opexLabel,
          _discountLabel: _discountLabel,
          _showDiscount: _showDiscount,
          _positionLabel: _positionLabel,
          _tierLabel: _tierLabel,
          _rruSetLabel: _rruSetLabel,
          _segments: _segments,
          _splitDate: _splitDate,
          _splitTooltip: _splitTooltip,
          _composeCaption: _composeCaption
        })
      })
    },

    // XOL-416: `unassigned_sharer_no` can arrive WITH a full set of rows — it is a
    // banner over real months, not an empty state. Gate every unpriced affordance
    // on this, and NEVER on the sharer's number: a By-Qty sharer at #0 bills
    // normally from the live sharer count and must render untouched.
    //
    // XOL-464: this stays NARROW on purpose — it drives only the Sharer-#-0
    // caption sentence. A strict violation (position or term) also dashes rows
    // and nulls totals, but through a DIFFERENT reasonCode (or none at all), so
    // `isUnpriced` is false for it. Do not widen this to catch that case: it
    // would print "while the Sharer # is 0" on a sharer whose number is fine,
    // manufacturing the exact false statement this computed exists to avoid.
    // See `totalsUnknown` and `anyUnpricedRow` below for the reason-agnostic
    // gates the strict case actually needs.
    isUnpriced: function () {
      return this.reasonCode === 'unassigned_sharer_no'
    },

    // XOL-464: whether ANY displayed row is dashed, regardless of why. Drives
    // only the column-level "Unpriced" tag in the Amount header — a strict
    // violation dashes the tail of the table through the same `priced: false`
    // grammar as the unassigned-Sharer-# case, but `isUnpriced` (above) stays
    // false for it, so the tag needs its own, broader gate.
    anyUnpricedRow: function () {
      return this.rows.some(function (r) { return r.priced === false })
    },

    // XOL-433: the banner is NOT `isUnpriced`. That computed stays narrow because
    // it drives things that are only true of the unassigned-Sharer-# case
    // specifically — the Sharer-#-0 caption sentence (see `totalsUnknown` and
    // `anyUnpricedRow` for the broader, reason-agnostic gates that drive the
    // column tag and the rest of the caption chain as of XOL-464). The banner is
    // a different question: "did the API tell us something is wrong, on a screen
    // that is showing rows?"
    //
    // Before XOL-433 the API suppressed the rows whenever it sent `no_rate` /
    // `no_billing_start`, so `reason` reached the user through the isEmpty branch
    // at the top of this template. Once those rows are emitted, isEmpty is false
    // and that branch stops rendering — so without this the sentence would be
    // fetched and silently dropped, which is XOL-425's bug on a new path.
    //
    // Requires `reason` as well as the code: an empty red panel explains nothing.
    // On this endpoint the prose predates the code (`reason` shipped in XOL-298,
    // `reasonCode` was added beside it in XOL-416), so code-without-prose is not a
    // response the API produces — no invented fallback copy for a state that does
    // not occur.
    showReasonBanner: function () {
      return !this.isEmpty && !!this.reasonCode && !!this.reason
    },

    totalsDisplay: function () {
      var self = this

      // A null total is UNKNOWN, not zero, whatever produced it. formatMoney would
      // render it RM0.00 (Number(null) is 0), and "Lifetime RM0.00" is a single
      // confident number a finance user acts on.
      //
      // The API sends totals as an object with null FIELDS rather than a null
      // object, deliberately: nulling the container would change the response
      // shape per state and break every consumer's field access. So the old
      // `if (!this.totals)` guard — which only caught an absent object — never
      // fired for the unpriced case and let all three print RM0.00.
      var money = function (value) {
        if (value === null || value === undefined) {
          return '—'
        }
        return self.formatMoney(value, 2)
      }

      if (!this.totals) {
        return { billedToDate: '—', projectedRemaining: '—', lifetime: '—' }
      }
      return {
        billedToDate: money(this.totals.billedToDate),
        projectedRemaining: money(this.totals.projectedRemaining),
        lifetime: money(this.totals.lifetime)
      }
    },

    // XOL-464: a strict violation (position or term) nulls all three totals but
    // leaves `reasonCode` null — so the caption can't key off a reason code the
    // way `isUnpriced` does. This keys off the totals themselves instead: if any
    // of the three is null, something is unknown, whatever produced it.
    //
    // `!t` returning false is deliberate, not an oversight: an absent `totals` is
    // the pre-fetch / legacy shape, already handled by `totalsDisplay`'s own
    // `if (!this.totals)` guard, and must not be reported as "unknown" here too —
    // that would fire this caption on every load before the fetch resolves.
    totalsUnknown: function () {
      var t = this.totals
      if (!t) return false
      return t.billedToDate === null || t.projectedRemaining === null || t.lifetime === null
    }
  },

  created: function () {
    this.load()
  },

  methods: {
    load: function () {
      if (!this.id) {
        this.loaded = true
        return
      }

      var self = this
      this.loading = true
      this.errored = false
      this.loaded = false

      this.fetchData(
        'licensees/' + this.id + '/fee-ledger',
        function (data) {
          var d = data && typeof data === 'object' && !Array.isArray(data) ? data : {}
          self.rows = Array.isArray(d.rows) ? d.rows : []
          self.totals = d.totals || null
          self.window = d.window || null
          self.licensee = d.licensee || null
          self.reason = d.reason || null
          self.reasonCode = d.reasonCode || null
          self.issues = Array.isArray(d.issues) ? d.issues : []
          self.loading = false
          self.loaded = true
        },
        'fee timeline',
        function (_err) {
          self.errored = true
          self.loading = false
          self.loaded = true
          // XOL-464: a REFETCH (not just a first load) can fail after a prior
          // success already populated `issues`. Without this, the hoisted
          // <sharer-billing-issues> banner (root-level, unconditional on
          // `errored`) would keep asserting a specific billing diagnosis
          // sourced from data we just failed to refresh, directly above the
          // "Couldn't load the fee timeline" alert admitting we don't have
          // current data. Clear the state at its source, the same call made
          // for `fromSharerError` / the `strictPosition` watcher elsewhere in
          // this ticket (watcher deleted in XOL-487) — never gate a stale
          // value in the template when it can be cleared where it lives.
          self.issues = []
        }
      )
    },

    // XOL-319: under "Qty" billing the Position determinant is really a sharer
    // count, so label it "Count"; "Position" everywhere else. Case-insensitive
    // so 'Qty'/'qty' both flip.
    positionKeyLabel: function (row) {
      var mode = row && row.billing_mode
      return String(mode).toLowerCase() === 'qty' ? 'Count' : 'Position'
    },

    rowClassName: function (obj) {
      var row = obj.row
      var classes = []
      if (row.isProjection) {
        classes.push('timeline-row--projection')
      }
      if (row.__boundary) {
        classes.push('timeline-row--boundary')
      }
      return classes.join(' ')
    }
  }
}
</script>

<style scoped>
.timeline-totals {
  margin-bottom: 8px;
}

.timeline-totals__stat {
  padding: 8px 0;
}

.timeline-totals__label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 2px;
}

.timeline-totals__label--muted {
  color: #909399;
}

.timeline-totals__value {
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}

.timeline-totals__value--muted {
  color: #909399;
}

.timeline-totals__value--bold {
  font-weight: 600;
}

.timeline-caption {
  font-size: 12px;
  margin-bottom: 8px;
}

.timeline-boundary-marker {
  display: block;
  font-size: 11px;
  color: #909399;
  margin-bottom: 2px;
}

.timeline-flag {
  margin-left: 4px;
  cursor: default;
}

::v-deep .fee-cell {
  font-variant-numeric: tabular-nums;
}

::v-deep .timeline-row--projection td {
  background: #fafafa;
  color: #909399;
}

::v-deep .timeline-row--boundary td {
  border-top: 2px solid #dcdfe6 !important;
}

.timeline-expand {
  padding: 8px 16px;
}

.timeline-expand__caption {
  font-size: 12px;
  margin-bottom: 8px;
}

.timeline-expand__item {
  margin-bottom: 6px;
  display: flex;
  gap: 6px;
}

.timeline-expand__key {
  font-size: 12px;
  min-width: 100px;
  flex-shrink: 0;
}

.timeline-expand__val {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

/* XOL-520 — segments block. Deliberate departure from `.timeline-expand__key`'s
   `.text-muted` (2.53:1, fails WCAG AA at 12px — src/assets/css/app.css:239):
   heading / caption / total label use #606266 (5.7:1); dates, day counts and
   money use #303133 (15.9:1); only the footnote, genuinely secondary, uses
   #909399 (4.6:1, passes). Do not revert to .text-muted for "consistency" with
   the neighbouring determinant grid — that grid's contrast failure is
   pre-existing and out of scope here, not a pattern to extend. */
.timeline-segments__heading {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}

.timeline-segments__caption {
  font-size: 12px;
  color: #606266;
  margin-bottom: 8px;
}

.timeline-segments__row {
  margin-bottom: 6px;
}

.timeline-segments__tier-col {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Mirrors SharerFeeSchedule.vue's tier-tag shape (background #f4f4f5), but with
   text #606266, not the shared token's #909399 (2.9:1) — redefined locally
   rather than editing the shared tier-n token. No `tier-1` "active" variant
   carried across: no tier is active in a historical month, and the green
   would falsely imply currency. */
.timeline-segments__tier {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  background: #f4f4f5;
  color: #606266;
}

/* D.6 — the tier-less line (the override row) holds its column width via a
   zero-background spacer, so ranges stay aligned when a tiered and an override
   panel are open together. No text, no background — a chip reading "T—" or an
   empty grey box are both rejected (see the template comment). */
.timeline-segments__tier-spacer {
  display: inline-block;
  min-width: 26px;
}

.timeline-segments__range {
  font-size: 12px;
  color: #303133;
  font-variant-numeric: tabular-nums;
}

/* §E required CSS — without `gap` the basis text and amount can sit flush
   against each other at narrow widths. */
.timeline-segments__basis-col {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.timeline-segments__basis {
  font-size: 12px;
  color: #303133;
  font-variant-numeric: tabular-nums;
}

/* §E required CSS — without this the AMOUNT is the flex item that shrinks, and
   a figure like "RM2,325.00" can wrap mid-number at `sm`. Survivable at 7 rows;
   not at ~235. */
.timeline-segments__amount {
  font-size: 12px;
  color: #303133;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  white-space: nowrap;
}

.timeline-segments__total-row {
  border-top: 1px solid #ebeef5;
  padding-top: 4px;
  margin-top: 2px;
  margin-bottom: 8px;
}

.timeline-segments__total-label {
  font-size: 12px;
  color: #606266;
}

.timeline-segments__total-amount {
  font-size: 12px;
  font-weight: 600;
  color: #303133;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.timeline-segments__footnote {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

/* Required at Element's `xs` breakpoint (<768px, viewport-keyed, not
   container-keyed): the forced `padding: 20px 50px` on
   `.el-table__expanded-cell[class*=cell]` plus this panel's own `8px 16px`
   leaves ~188px of content at a 320px viewport, and a segment date range
   (~150px) wraps mid-range without this override. Matches Element's own
   `[class*=cell]` specificity hack, or it loses. Expected side effect: the
   determinant grid above also gains room at this width — an improvement,
   not a regression to flag. */
@media (max-width: 768px) {
  ::v-deep .el-table__expanded-cell[class*=cell] {
    padding: 12px 16px;
  }
}
</style>
